import { Component, ElementRef, HostBinding, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSidenav } from '@angular/material/sidenav';

import { combineLatest, distinctUntilChanged, Observable, of, Subscription, tap, TeardownLogic } from 'rxjs';

import { ConfirmationDialogService, MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs';

import { ApplicantService } from 'services/applicant/applicant.service';
import { AuthService } from 'services/auth/auth.service';
import { ErrorService } from 'services/error/error.service';
import { NavMenuService } from '../navmenu/navmenu.service';
import { NavMenuItem } from '../navmenu/navmenu.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { UserService } from 'services/user/user.service';
import { UserInfo } from 'services/user/user.service.class';
import { BUILD_NOTES, BUILD_TIME, VERSION_NUMBER } from 'services/version/version.service';
import { WebApplicationService } from 'services/webapp/webapp.service';
import { SystemConfigInfo } from 'services/webapp/webapp.service.class';

import { RouteMenus, RoutePaths } from 'routes/route.roles';
import { AllMenuItems } from '../navmenu/menus';
import { ApplicantInfo } from 'services/applicant/applicant.service.class';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    @HostBinding('class.smalldisplay') SmallDisplay: boolean;

    @ViewChild(MatSidenav) set sideNav(_nav: MatSidenav | null) {
        this._sideNav = _nav;

        if (this._sideNav) {
            setTimeout(() => {
                if (this._sideNav) this._sideNav.mode = this._sideNav.mode;
            });
        }
    }
    get sideNav() {
        return this._sideNav;
    }
    private _sideNav: MatSidenav | null = null;

    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    AboutFn: () => Observable<boolean>;
    LogoutFn: () => Observable<boolean>;

    Application: ApplicantInfo | null = null;
    BuildNotes: string;
    BuildTime: string;
    CurrentRole: string;
    DevSite: boolean;
    LastSizeCode: string;
    LastSmallDisplay: boolean;
    MenuItems: NavMenuItem[] = [];
    MenuOpen = true;
    SizeCode: string;
    UpdateAvailable: boolean;
    User: UserInfo | null = null;
    VersionLoaded: string;
    ViewReady: boolean;

    constructor(
        private appSvc: ApplicantService,
        private authSvc: AuthService,
        private confDlgSvc: ConfirmationDialogService,
        private datePipe: DatePipe,
        private errorSvc: ErrorService,
        private msgBoxSvc: MessageBoxDialogService,
        private navMenuSvc: NavMenuService,
        private route: ActivatedRoute,
        private router: Router,
        private screenSizeSvc: ScreenSizeService,
        private userSvc: UserService,
        private webappSvc: WebApplicationService,
        private dlg: MessageBoxDialogService
    ) {
        this.AboutFn = this.About.bind(this);
        this.BuildNotes = BUILD_NOTES;
        this.CurrentRole = '';
        this.DevSite = false;
        this.LastSizeCode = '';
        this.LogoutFn = this.Logout.bind(this);
        this.SizeCode = '';
        this.SmallDisplay = false;
        this.LastSmallDisplay = false;
        this.UpdateAvailable = false;
        this.VersionLoaded = VERSION_NUMBER;
        this.ViewReady = false;

        const btime = BUILD_TIME && new Date(BUILD_TIME);
        this.BuildTime = (btime && this.datePipe.transform(btime, 'MMM. dd, yyyy h:mm a')) || '';

        // Subscribe to media changes
        this.subrelease = this.screenSizeSvc.ScreenSizeSub.subscribe((size) => {
            this.SmallDisplay = size.IsSmallDisplay;
            this.SizeCode = size.Alias;

            this.MenuOpen = !this.SmallDisplay;

            this.LastSmallDisplay = this.SmallDisplay;
        });

        // Subscribe to checks for a version upgrade
        this.subrelease = this.authSvc.HasVersionUpdate.subscribe((value) => {
            // Set the upgrade alert banner (or not)
            this.UpdateAvailable = value;
        });

        // Subscribe to the user subject and application to keep user/application in sync.
        this.subrelease = combineLatest([this.userSvc.UserSubject, this.appSvc.Application]).subscribe((info) => {
            this.User = info[0];
            this.Application = info[1];

            // If we have a user info loaded, but we have a null application, it is likely because of a refresh of the page and we should
            // attempt to reload the application.
            if (this.User?.AuthToken && this.Application == null) {
                // Reload the user's application.
                this.appSvc.RefreshApplication();
            }
        });

        // Subscribe to the nav menu service to keep the menu options in sync.
        this.subrelease = this.navMenuSvc.MenuItems.subscribe((menus) => {
            // Update the available menu items.
            this.MenuItems = menus || [];
        });

        // Subscribe to system online status updates. If the system goes offline, this may cause us to reroute to the offline route.
        this.subrelease = this.authSvc.SystemOnlineSubject.pipe(distinctUntilChanged((p, c) => p?.Status === c?.Status)).subscribe(
            (status) => {
                if (status?.Status) {
                    this.ViewReady = true;
                    // Load everything needed for the app to run initially.
                    // combineLatest([this.appSvc.GetSystemConfig()]).subscribe({
                    //     next: (_) => {
                    this.ViewReady = true;

                    // If the current user has no auth token, we can now go to the landing route to handle this.
                    if (
                        !this.userSvc?.User?.AuthToken &&
                        this.router.url !== '/' &&
                        !this.router.url.includes(RoutePaths.Landing) &&
                        !this.router.url.includes(RoutePaths.StaffSSO)
                    ) {
                        this.router.navigate([`/${RoutePaths.Landing}`]);
                    }
                    // },
                    // error: (err) => {
                    //     this.ViewReady = true;
                    //     this.errorSvc.HandleServiceError(err);
                    // },
                    // }
                    // );
                } else {
                    // If the system is offline, AND if there is no current user authtoken, go to the down/offline route.
                    this.ViewReady = true;

                    if (!this.userSvc?.User?.AuthToken) {
                        this.router.navigate([`/${RoutePaths.Offline}`]);
                    }
                }
            }
        );

        // Conduct an initial test of the connection, to set the proper status.
        this.authSvc.TestConnection().subscribe(
            (result) => {
                this.authSvc.SetSystemOnlineStatus(result);
            },
            (err) => {
                // A failure would indicate the system is offline.
                this.authSvc.SetSystemOnlineStatus({
                    Status: false,
                    SystemMessage: '',
                    JobsVersion: VERSION_NUMBER,
                });
            }
        );
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
    }

    ngOnInit(): void {}

    // #region About Method

    About(): Observable<boolean> {
        const self = this;

        if (self.SmallDisplay) {
            self.MenuOpen = false;
        }

        let msg = `The Applicant Registration System (ARS) is a product of the Polk County Public Schools system. The system provides a single convenient location for all interested applicants to create, maintain, and submit their applications for employment, browse available vacancies across the district, and apply for specific vacancies.<br /><br /><strong>ARS Version: </strong>${self.VersionLoaded}<br /><strong>Build Date/Time: </strong>${self.BuildTime}`;

        if (self.BuildNotes) {
            msg += `<br /><strong>Build Notes: </strong><br />` + self.BuildNotes;
        }

        return self.msgBoxSvc.Open({
            TitleText: 'About ARS System',
            MessageText: msg,
        });
    }

    // #endregion

    // #region Get Update Method

    GetUpdate(): void {
        // Get a confirmation
        this.confDlgSvc
            .Open({
                // .OpenConfirmationDialog({
                TitleText: 'Site Update Available',
                MessageText:
                    `An update is available to the Applicant Registration System (ARS). Updates typically fix issues reported ` +
                    `by users or offer new features. If you are encountering problems with the site, it is recommended that ` +
                    `you load the update for the website at this time.<br /><br /><strong>Note: </strong>Loading the update ` +
                    `will refresh your page and you will lose any unsubmitted changes.`,
                OKText: 'Load Update',
                CancelText: 'CANCEL',
            })
            .subscribe((res) => {
                if (res === true) {
                    // User is opting to load the update. Refresh the page without caching.
                    window.location.href = `${window.location.protocol}//${window.location.host}`;
                }
            });
    }

    // #endregion

    // #region Go Home Method

    GoHome() {
        if (!this.userSvc.User?.AuthToken) {
            this.router.navigate([`/${RoutePaths.Landing}`]);
        } else {
            this.router.navigate([`/${RoutePaths.Home}`]);
        }
    }

    // #endregion

    // #region Logout Method

    Logout(): Observable<boolean> {
        const self = this;

        // If we don't have a tracker in place, and we're on xs size code, we need to ensure a dialog will open.
        let confObs: Observable<boolean> | null = null;

        // if (!self.unsavedChangesSvc.HasTracker() && self.SizeCode === 'xs') {
        //     confObs = null; // Ensures it will defer to dialog.
        // } else {
        //     confObs = self.unsavedChangesSvc.CheckTracker?.(); // This will attempt to use a check tracker.
        // }

        return (
            confObs ||
            self.confDlgSvc.Open({
                TitleText: 'Confirmation Requested',
                MessageText:
                    'You are about to log out of the Applicant Registration System (ARS) system. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            })
        ).pipe(
            tap((result) => {
                if (result === true) {
                    // The tracker says it's okay to logout, Go ahead and logout without further confirmation.
                    // Disable change tracking first.
                    // self.unsavedChangesSvc.ClearTracking();

                    // Now perform the logout.
                    self.authSvc.Logout();
                }
            })
        );
    }

    // #endregion

    // #region Navigate Method

    Navigate(event: any) {
        if (this.SmallDisplay) {
            this.MenuOpen = false;
        }
    }

    // #endregion

    // #region Toolbar About Method

    ToolbarAbout(): void {
        this.About().subscribe();
    }

    // #endregion

    // #region Toolbar Home

    ToolbarHome(): void {
        this.router.navigate([`/${RoutePaths.Home}`]);
    }

    // #endregion

    // #region Toolbar Logout Method

    ToolbarLogout(): void {
        this.Logout().subscribe();
    }

    // #endregion

    // #region Toggle Nav Menu Method

    ToggleNavMenu() {
        this.MenuOpen = !this.MenuOpen;
    }

    // #endregion
}
